import {
  CopyOutlined,
  DeleteOutlined,
  KeyOutlined,
  ReloadOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { getMe } from '../../actions/auth';
import apiTokenApi from '../../apis/apiToken';
import config from '../../config/config';
import { RESPONSE_ERROR } from '../../constants/error';
import { ButtonType } from '../../constants/style';
import { ApiToken } from '../../interfaces/ApiToken';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import { FlexContainer, PageContainer } from '../../styles/Global';
import Button from '../Button/Button';
import PageHeader from '../PageHeader/PageHeader';

const { Paragraph, Text, Title } = Typography;

const ApiAccess = () => {
  const dispatch = useDispatch();
  const { authUser, currentUser } = useSelector(
    (state: RootState) => state.auth as IAuthState
  );
  // Role from the JWT so the page does not flicker while /user/me loads.
  const isAdmin = authUser?.role === Role.ADMIN;
  // Unknown (still loading) counts as "set": show the form, the API decides.
  const hasApiPasswordSet = currentUser?.hasApiAccessPassword !== false;

  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  // The admin-issued API access password is kept in memory for this visit
  // only and sent as a header on every token call. null = page locked.
  const [apiPassword, setApiPassword] = useState<string | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);

  const unlocked = isAdmin || apiPassword !== null;

  const relock = useCallback(
    (reason: string) => {
      setApiPassword(null);
      setTokens([]);
      setRevealedToken(null);
      setLockError(reason);
      // Refresh hasApiAccessPassword: an admin may have removed the password.
      dispatch(getMe());
    },
    [dispatch]
  );

  // A 403 means the API access password was rejected (wrong, removed or
  // changed by an admin): lock the page again instead of toasting.
  const handleTokenError = useCallback(
    (e: any, fallback: string) => {
      const msg = e?.message || fallback;
      if (!isAdmin && e?.statusCode === RESPONSE_ERROR.FORBIDDEN) {
        relock(msg);
      } else {
        message.error(msg);
      }
    },
    [isAdmin, relock]
  );

  const loadTokens = useCallback(
    async (password?: string) => {
      setLoading(true);
      try {
        const data = await apiTokenApi.listTokens(password);
        setTokens(data);
      } catch (e: any) {
        handleTokenError(e, 'Không tải được danh sách API token');
      } finally {
        setLoading(false);
      }
    },
    [handleTokenError]
  );

  useEffect(() => {
    if (isAdmin) {
      loadTokens();
    } else {
      dispatch(getMe());
    }
  }, [isAdmin, loadTokens, dispatch]);

  const handleUnlock = async () => {
    if (!passwordInput) {
      setLockError('Vui lòng nhập mật khẩu truy cập API.');
      return;
    }
    setUnlocking(true);
    try {
      const data = await apiTokenApi.listTokens(passwordInput);
      setTokens(data);
      setApiPassword(passwordInput);
      setPasswordInput('');
      setLockError(null);
    } catch (e: any) {
      setLockError(e?.message || 'Không mở khoá được. Vui lòng thử lại.');
    } finally {
      setUnlocking(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const created = await apiTokenApi.createToken(
        tokenName.trim() ? { name: tokenName.trim() } : {},
        apiPassword ?? undefined
      );
      setRevealedToken(created.token);
      setTokenName('');
      message.success('Đã tạo API token');
      await loadTokens(apiPassword ?? undefined);
    } catch (e: any) {
      handleTokenError(e, 'Không tạo được API token');
    } finally {
      setCreating(false);
    }
  };

  const handleRotate = (token: ApiToken) => {
    Modal.confirm({
      title: 'Đổi (rotate) token này?',
      content:
        'Token hiện tại sẽ ngừng hoạt động ngay lập tức. Token mới chỉ được hiển thị một lần.',
      okText: 'Đổi token',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const created = await apiTokenApi.rotateToken(
            token.id,
            apiPassword ?? undefined
          );
          setRevealedToken(created.token);
          message.success('Đã đổi API token');
          await loadTokens(apiPassword ?? undefined);
        } catch (e: any) {
          handleTokenError(e, 'Không đổi được API token');
        }
      },
    });
  };

  const handleRevoke = (token: ApiToken) => {
    Modal.confirm({
      title: 'Thu hồi token này?',
      content:
        'Ứng dụng đang dùng token này sẽ mất quyền truy cập ngay lập tức.',
      okText: 'Thu hồi',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiTokenApi.revokeToken(token.id, apiPassword ?? undefined);
          message.success('Đã thu hồi API token');
          await loadTokens(apiPassword ?? undefined);
        } catch (e: any) {
          handleTokenError(e, 'Không thu hồi được API token');
        }
      },
    });
  };

  const copyToken = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success('Đã sao chép');
    } catch {
      message.error('Không sao chép được');
    }
  };

  const columns: ColumnsType<ApiToken> = [
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      render: (name?: string) => name || '—',
    },
    {
      title: 'Token',
      dataIndex: 'tokenPrefix',
      key: 'tokenPrefix',
      render: (prefix: string) => <Text code>{prefix}…</Text>,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Lần dùng gần nhất',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (value?: string) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : 'Chưa dùng',
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type={ButtonType.OUTLINE}
            onClick={() => handleRotate(record)}
          >
            <ReloadOutlined /> Đổi token
          </Button>
          <Button type={ButtonType.DANGER} onClick={() => handleRevoke(record)}>
            <DeleteOutlined /> Thu hồi
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader title="Truy cập API" />

      <Content>
        <Section>
          <Title level={4}>Giới thiệu</Title>
          <Paragraph>
            API công khai giúp bạn (seller) tích hợp hệ thống HD Sticker vào
            app / website / công cụ riêng của mình: lấy dữ liệu đơn hàng (tương
            tự chức năng xuất CSV) và <Text strong>tạo đơn hàng loạt</Text>{' '}
            bằng JSON cho mọi loại sản phẩm — thay cho việc upload CSV trên
            trang quản lý đơn.
          </Paragraph>
          <Paragraph type="secondary">
            Mỗi tài khoản seller có thể tạo tối đa <Text strong>5</Text> token
            đang hoạt động. Token dạng <Text code>stk_…</Text>, chỉ hiển thị{' '}
            <Text strong>một lần</Text> khi tạo hoặc đổi. Hãy lưu token ở nơi
            an toàn (ví dụ biến môi trường trên server); không đưa vào frontend
            công khai hoặc commit lên Git.
          </Paragraph>
          <Paragraph type="secondary">
            Để quản lý token, seller cần nhập thêm{' '}
            <Text strong>mật khẩu truy cập API</Text> — mật khẩu riêng do admin
            cấp, khác mật khẩu đăng nhập và chỉ dùng trên trang này (không dùng
            khi gọi API). Chỉ admin mới đặt lại hoặc gỡ được mật khẩu này; nhập
            sai 5 lần sẽ bị khoá 15 phút.
          </Paragraph>
        </Section>

        <Section>
          <Title level={4}>
            <KeyOutlined /> Token của bạn
          </Title>
          <Paragraph type="secondary">
            Gửi token trong header{' '}
            <Text code>Authorization: Bearer stk_…</Text> khi gọi các endpoint
            công khai. Nếu nghi ngờ token bị lộ, hãy <Text strong>đổi</Text>{' '}
            hoặc <Text strong>thu hồi</Text> ngay.
          </Paragraph>

          {!unlocked ? (
            hasApiPasswordSet ? (
              <>
                <Paragraph type="secondary">
                  Nhập <Text strong>mật khẩu truy cập API</Text> do admin cấp
                  (khác mật khẩu đăng nhập) để xem và quản lý token. Mật khẩu
                  chỉ được giữ trong lần truy cập này.
                </Paragraph>
                {lockError && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={lockError}
                  />
                )}
                <CreateRow>
                  <Input.Password
                    placeholder="Mật khẩu truy cập API"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    onPressEnter={handleUnlock}
                    autoComplete="off"
                    style={{ maxWidth: 280 }}
                  />
                  <Button
                    type={ButtonType.PRIMARY}
                    onClick={handleUnlock}
                    loading={unlocking}
                  >
                    <UnlockOutlined /> Mở khoá
                  </Button>
                </CreateRow>
              </>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Tài khoản chưa được cấp mật khẩu truy cập API"
                description="Vui lòng liên hệ admin để được cấp mật khẩu trước khi quản lý API token."
              />
            )
          ) : (
            <>
              <CreateRow>
                <Input
                  placeholder="Tên token (không bắt buộc)"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  maxLength={100}
                  style={{ maxWidth: 280 }}
                />
                <Button
                  type={ButtonType.PRIMARY}
                  onClick={handleCreate}
                  loading={creating}
                >
                  Tạo token
                </Button>
              </CreateRow>

              {revealedToken && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Sao chép token ngay — bạn sẽ không xem lại được"
                  description={
                    <FlexContainer style={{ gap: 8, alignItems: 'center' }}>
                      <TokenValue code copyable={false}>
                        {revealedToken}
                      </TokenValue>
                      <Button
                        type={ButtonType.OUTLINE}
                        onClick={() => copyToken(revealedToken)}
                      >
                        <CopyOutlined /> Sao chép
                      </Button>
                    </FlexContainer>
                  }
                  closable
                  onClose={() => setRevealedToken(null)}
                />
              )}

              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={tokens}
                pagination={false}
                locale={{ emptyText: 'Chưa có token nào' }}
              />
            </>
          )}
        </Section>

        <Section>
          <Title level={4}>Xác thực (Authentication)</Title>
          <Paragraph>
            Mọi request tới API công khai đều cần header:
          </Paragraph>
          <Pre>{`Authorization: Bearer <API_TOKEN>`}</Pre>
          <BulletList>
            <li>
              <Text code>&lt;API_TOKEN&gt;</Text> là chuỗi đầy đủ bắt đầu bằng{' '}
              <Text code>stk_</Text> (không phải mật khẩu đăng nhập web).
            </li>
            <li>
              Token chỉ xem / tạo được đơn của <Text strong>chính seller</Text>{' '}
              sở hữu token — không thể xem hay tạo đơn cho seller khác.
            </li>
            <li>
              Nếu thiếu / sai token, API trả về{' '}
              <Text code>401 Unauthorized</Text>.
            </li>
          </BulletList>
        </Section>

        <Section>
          <Title level={4}>Endpoint</Title>
          <Paragraph>
            Base URL: <Text code>{config.API_HOST}</Text>
          </Paragraph>

          <SubTitle>1. Danh sách đơn hàng</SubTitle>
          <Pre>{`GET ${config.API_HOST}/v1/orders`}</Pre>
          <Paragraph>
            Trả về danh sách đơn của bạn, phân trang. Hỗ trợ lọc theo trạng thái,
            ngày, sản phẩm, tracking, quốc gia…
          </Paragraph>

          <ParamTable>
            <thead>
              <tr>
                <th>Tham số</th>
                <th>Kiểu</th>
                <th>Mô tả</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Text code>page</Text>
                </td>
                <td>number</td>
                <td>Trang hiện tại (mặc định: 1)</td>
              </tr>
              <tr>
                <td>
                  <Text code>perPage</Text>
                </td>
                <td>number</td>
                <td>Số đơn mỗi trang (mặc định: 20, tối đa: 100)</td>
              </tr>
              <tr>
                <td>
                  <Text code>search</Text>
                </td>
                <td>string</td>
                <td>Tìm theo mã đơn / thông tin liên quan</td>
              </tr>
              <tr>
                <td>
                  <Text code>status</Text>
                </td>
                <td>string | string[]</td>
                <td>
                  Lọc trạng thái: <Text code>pending</Text>,{' '}
                  <Text code>processing</Text>, <Text code>shipped</Text>,{' '}
                  <Text code>reshipped</Text>, <Text code>cancelled</Text>. Có
                  thể gửi nhiều giá trị.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>product</Text>
                </td>
                <td>string</td>
                <td>
                  Loại sản phẩm, ví dụ: <Text code>sticker</Text>,{' '}
                  <Text code>magnet_sticker</Text>, <Text code>iphone</Text>,{' '}
                  <Text code>poster</Text>, <Text code>uvdtf</Text>,{' '}
                  <Text code>badge_reel</Text>,{' '}
                  <Text code>glass_suncatcher</Text>,{' '}
                  <Text code>squishy_belly</Text>,{' '}
                  <Text code>card_skin</Text>, <Text code>ornament</Text>
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>country</Text>
                </td>
                <td>string</td>
                <td>Mã quốc gia (ISO), ví dụ <Text code>US</Text></td>
              </tr>
              <tr>
                <td>
                  <Text code>fromDate</Text> / <Text code>toDate</Text>
                </td>
                <td>string</td>
                <td>Khoảng ngày tạo đơn (ISO date, inclusive)</td>
              </tr>
              <tr>
                <td>
                  <Text code>byDate</Text>
                </td>
                <td>
                  <Text code>ASC</Text> | <Text code>DESC</Text>
                </td>
                <td>Sắp xếp theo ngày tạo</td>
              </tr>
              <tr>
                <td>
                  <Text code>tracking</Text>
                </td>
                <td>string</td>
                <td>
                  <Text code>no_tracking</Text>,{' '}
                  <Text code>tracking_only</Text>,{' '}
                  <Text code>shipment_only</Text>, hoặc{' '}
                  <Text code>needs_shippo_purchase</Text>
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>shipmentStatus</Text>
                </td>
                <td>string</td>
                <td>
                  Trạng thái vận chuyển, ví dụ <Text code>in_transit</Text>,{' '}
                  <Text code>delivered</Text>…
                </td>
              </tr>
            </tbody>
          </ParamTable>

          <SubTitle>2. Chi tiết một đơn</SubTitle>
          <Pre>{`GET ${config.API_HOST}/v1/orders/:id`}</Pre>
          <Paragraph>
            <Text code>:id</Text> là UUID nội bộ của đơn (trường{' '}
            <Text code>id</Text> trong response danh sách), không phải mã đơn
            seller (<Text code>orderId</Text>).
          </Paragraph>

          <SubTitle>
            3. Tạo đơn hàng loạt (bulk) <NewTag color="green">New</NewTag>
          </SubTitle>
          <Pre>{`POST ${config.API_HOST}/v1/orders/bulk
Content-Type: application/json`}</Pre>
          <Paragraph>
            Tạo tối đa <Text strong>100 đơn</Text> trong một request, cho mọi
            loại sản phẩm. Body là JSON gồm:
          </Paragraph>
          <ParamTable>
            <thead>
              <tr>
                <th>Tham số</th>
                <th>Kiểu</th>
                <th>Mô tả</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Text code>storeId</Text>
                </td>
                <td>string</td>
                <td>
                  Không bắt buộc — store nhận đơn. Bỏ trống thì dùng store mặc
                  định của bạn.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>orders[]</Text>
                </td>
                <td>array</td>
                <td>Danh sách đơn (1–100), mỗi đơn gồm các trường bên dưới.</td>
              </tr>
            </tbody>
          </ParamTable>

          <Paragraph>
            Mỗi phần tử trong <Text code>orders</Text>:
          </Paragraph>
          <ParamTable>
            <thead>
              <tr>
                <th>Tham số</th>
                <th>Kiểu</th>
                <th>Mô tả</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Text code>orderId</Text>
                </td>
                <td>string</td>
                <td>
                  Bắt buộc. Mã đơn của bạn — phải duy nhất trong các đơn chưa
                  huỷ và không được trùng trong cùng request.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>product</Text>
                </td>
                <td>string</td>
                <td>
                  Bắt buộc. Một trong: <Text code>sticker</Text>,{' '}
                  <Text code>magnet_sticker</Text>, <Text code>uvdtf</Text>,{' '}
                  <Text code>card_skin</Text>, <Text code>phone_case</Text>,{' '}
                  <Text code>poster</Text>, <Text code>badge_reel</Text>,{' '}
                  <Text code>glass_suncatcher</Text>,{' '}
                  <Text code>squishy_belly</Text>, <Text code>ornament</Text>.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>address</Text>
                </td>
                <td>string</td>
                <td>
                  Bắt buộc. Địa chỉ nhận hàng nhiều dòng (xuống dòng bằng{' '}
                  <Text code>\n</Text>): tên người nhận / số nhà &amp; đường /{' '}
                  <Text code>CITY, ST ZIP</Text> — giống form tạo đơn trên web.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>country</Text>
                </td>
                <td>string</td>
                <td>
                  Bắt buộc. <Text code>us</Text> hoặc <Text code>ca</Text> (chữ
                  thường).
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>trackingReference</Text>
                </td>
                <td>string</td>
                <td>
                  Mã vận đơn của bạn nếu tự mua label. Bắt buộc với{' '}
                  <Text code>glass_suncatcher</Text>.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>requestAdminPurchaseShipment</Text>
                </td>
                <td>boolean</td>
                <td>
                  Chỉ áp dụng cho <Text code>sticker</Text>,{' '}
                  <Text code>magnet_sticker</Text>, <Text code>uvdtf</Text>,{' '}
                  <Text code>card_skin</Text>. Không có tracking và không bật cờ
                  này → hệ thống tự mua label (EasyPost) ngay khi tạo, phí ship
                  cộng vào <Text code>totalCost</Text>. Bật <Text code>true</Text>{' '}
                  → đơn vào hàng chờ admin mua tracking. Sản phẩm khác bỏ qua
                  trường này.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>shipWithHardTube</Text>
                </td>
                <td>boolean</td>
                <td>
                  Chỉ <Text code>poster</Text>, <Text code>badge_reel</Text>,{' '}
                  <Text code>glass_suncatcher</Text>,{' '}
                  <Text code>squishy_belly</Text>, <Text code>ornament</Text>:
                  ship bằng ống cứng (+$1.5). Sản phẩm khác bỏ qua trường này.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>notes</Text>
                </td>
                <td>string</td>
                <td>Ghi chú cho đơn (không bắt buộc).</td>
              </tr>
              <tr>
                <td>
                  <Text code>designs[]</Text>
                </td>
                <td>array</td>
                <td>Bắt buộc, ít nhất 1 thiết kế (bảng dưới).</td>
              </tr>
            </tbody>
          </ParamTable>

          <Paragraph>
            Mỗi phần tử trong <Text code>designs</Text>:
          </Paragraph>
          <ParamTable>
            <thead>
              <tr>
                <th>Tham số</th>
                <th>Kiểu</th>
                <th>Mô tả</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Text code>designLink</Text>
                </td>
                <td>string</td>
                <td>
                  Bắt buộc. Link ảnh công khai (http/https) hoặc link chia sẻ
                  Google Drive — hệ thống tải về và tạo thiết kế mới. Gửi lại
                  đúng <Text code>designUrl</Text> mà API đã trả về để dùng lại
                  thiết kế có sẵn. Với <Text code>phone_case</Text> phải là ảnh
                  (PNG/JPG): hệ thống tự tạo file in theo model.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>size</Text>
                </td>
                <td>string</td>
                <td>
                  Bắt buộc. Tên size đúng như <Text code>GET /v1/sizes</Text>{' '}
                  trả về (mục 4). <Text code>phone_case</Text>: tên model, ví dụ{' '}
                  <Text code>iphone_15_pro</Text>. <Text code>ornament</Text>:{' '}
                  <Text code>default</Text>.
                </td>
              </tr>
              <tr>
                <td>
                  <Text code>quantity</Text>
                </td>
                <td>number</td>
                <td>Bắt buộc. Số nguyên ≥ 1.</td>
              </tr>
              <tr>
                <td>
                  <Text code>sku</Text>
                </td>
                <td>string</td>
                <td>SKU gắn với thiết kế (không bắt buộc).</td>
              </tr>
              <tr>
                <td>
                  <Text code>variant</Text>
                </td>
                <td>string</td>
                <td>
                  Bắt buộc với <Text code>badge_reel</Text> (
                  <Text code>belt_clip</Text>, <Text code>swivel_clip</Text>,{' '}
                  <Text code>topper</Text>), <Text code>glass_suncatcher</Text> (
                  <Text code>round</Text>, <Text code>oval</Text>,{' '}
                  <Text code>heart</Text>), <Text code>squishy_belly</Text> (
                  <Text code>pack_1</Text>, <Text code>pack_2</Text>,{' '}
                  <Text code>pack_3</Text>, <Text code>pack_5</Text>),{' '}
                  <Text code>card_skin</Text> (<Text code>standard</Text>,{' '}
                  <Text code>holographic</Text>), <Text code>ornament</Text> (
                  <Text code>ceramic</Text>, <Text code>glass</Text>,{' '}
                  <Text code>acrylic</Text>). Sản phẩm khác không được gửi
                  variant.
                </td>
              </tr>
            </tbody>
          </ParamTable>

          <Paragraph>Cách API xử lý:</Paragraph>
          <BulletList>
            <li>
              Toàn bộ request được kiểm tra trước. Chỉ cần một đơn sai
              (product / size / variant / country, mã đơn trùng, size hết
              hàng, thiếu tracking với glass_suncatcher…) → trả{' '}
              <Text code>400</Text> và <Text strong>không tạo đơn nào</Text>.
              Message ghi rõ đơn lỗi, ví dụ{' '}
              <Text code>
                Đơn thứ 2 (mã 1002): thiết kế thứ 1: Không tìm thấy size
                &quot;6in&quot;…
              </Text>
              ; lỗi sai kiểu dữ liệu trả về dạng mảng, ví dụ{' '}
              <Text code>orders.0.designs.1.quantity phải là số nguyên</Text>.
            </li>
            <li>
              Qua bước kiểm tra, từng đơn được tạo độc lập. Đơn gặp lỗi khi tạo
              (ví dụ không tải được link ảnh) nằm trong{' '}
              <Text code>failed</Text>; các đơn còn lại vẫn được tạo và trả về
              trong <Text code>created</Text> (cùng định dạng{' '}
              <Text code>PublicOrder</Text> bên dưới).
            </li>
            <li>
              Khi gửi lại, chỉ gửi các đơn trong <Text code>failed</Text>: mã
              đơn đã tạo sẽ bị từ chối nên không lo tạo trùng.
            </li>
            <li>
              Với đơn tự mua label, kiểm tra <Text code>status</Text> /{' '}
              <Text code>trackingReference</Text> trong{' '}
              <Text code>created</Text>: nếu mua label không thành công, đơn
              vẫn được tạo ở trạng thái <Text code>pending</Text> và chưa có
              tracking.
            </li>
          </BulletList>

          <Paragraph>Ví dụ request:</Paragraph>
          <Pre>{`{
  "orders": [
    {
      "orderId": "1001",
      "product": "sticker",
      "address": "JANE DOE\\n123 MAIN ST APT 4\\nRALEIGH, NC 27601",
      "country": "us",
      "designs": [
        {
          "designLink": "https://drive.google.com/file/d/FILE_ID/view?usp=sharing",
          "size": "3in",
          "quantity": 2,
          "sku": "CAT-01"
        },
        { "designLink": "https://example.com/dog.png", "size": "4in", "quantity": 1 }
      ]
    },
    {
      "orderId": "1002",
      "product": "card_skin",
      "address": "JOHN SMITH\\n88 KING ST W\\nTORONTO, ON M5H 1A1",
      "country": "ca",
      "trackingReference": "9400111899223197428490",
      "designs": [
        {
          "designLink": "https://example.com/skin.png",
          "size": "large_chip",
          "quantity": 1,
          "variant": "holographic"
        }
      ]
    }
  ]
}`}</Pre>
          <Paragraph>
            Response <Text code>200</Text>:
          </Paragraph>
          <Pre>{`{
  "created": [ /* PublicOrder */ ],
  "failed": [
    { "orderId": "1002", "message": "Failed to download design images" }
  ]
}`}</Pre>
          <Paragraph>
            Response <Text code>400</Text> (không tạo đơn nào):
          </Paragraph>
          <Pre>{`{
  "statusCode": 400,
  "message": "Đơn thứ 1 (mã 1001): thiết kế thứ 2: Không tìm thấy size \\"6in\\" cho sản phẩm \\"sticker\\". Các size hợp lệ: 1.5 in, 2in, 3in, 4in, 5in.",
  "error": "Bad Request"
}`}</Pre>

          <SubTitle>
            4. Danh sách size / variant của sản phẩm{' '}
            <NewTag color="green">New</NewTag>
          </SubTitle>
          <Pre>{`GET ${config.API_HOST}/v1/sizes?product=card_skin`}</Pre>
          <Paragraph>
            <Text code>product</Text> bắt buộc (cùng các giá trị như trường{' '}
            <Text code>product</Text> ở trên). Trả về các size đang bán của sản
            phẩm — dùng đúng chuỗi <Text code>size</Text> và{' '}
            <Text code>variant</Text> trong response khi tạo đơn:
          </Paragraph>
          <Pre>{`[
  {
    "size": "large_chip",
    "description": "Large Chip",
    "price": 1.65,
    "isOutOfStock": false,
    "variants": [
      { "variant": "standard", "price": 1.65, "isOutOfStock": false },
      { "variant": "holographic", "price": 2, "isOutOfStock": false }
    ]
  }
]`}</Pre>
          <Paragraph type="secondary">
            <Text code>variants</Text> rỗng với sản phẩm không có variant. Size
            / variant đang <Text code>isOutOfStock: true</Text> sẽ bị từ chối
            khi tạo đơn.
          </Paragraph>
        </Section>

        <Section>
          <Title level={4}>Định dạng response</Title>
          <Paragraph>
            Danh sách đơn trả về dạng phân trang:
          </Paragraph>
          <Pre>{`{
  "items": [ /* PublicOrder */ ],
  "page": 1,
  "perPage": 20,
  "total": 42,
  "pageCount": 3
}`}</Pre>
          <Paragraph>Mỗi đơn (<Text code>PublicOrder</Text>) gồm:</Paragraph>
          <ParamTable>
            <thead>
              <tr>
                <th>Trường</th>
                <th>Mô tả</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Text code>id</Text>
                </td>
                <td>UUID đơn (dùng cho endpoint chi tiết)</td>
              </tr>
              <tr>
                <td>
                  <Text code>orderId</Text>
                </td>
                <td>Mã đơn phía seller (nếu có)</td>
              </tr>
              <tr>
                <td>
                  <Text code>status</Text>
                </td>
                <td>Trạng thái đơn</td>
              </tr>
              <tr>
                <td>
                  <Text code>type</Text>
                </td>
                <td>Loại sản phẩm của đơn</td>
              </tr>
              <tr>
                <td>
                  <Text code>address</Text>, <Text code>country</Text>
                </td>
                <td>Địa chỉ giao hàng</td>
              </tr>
              <tr>
                <td>
                  <Text code>trackingReference</Text>,{' '}
                  <Text code>trackingUrl</Text>,{' '}
                  <Text code>shipmentStatus</Text>
                </td>
                <td>Thông tin vận đơn (nếu có)</td>
              </tr>
              <tr>
                <td>
                  <Text code>shippingCarrier</Text>,{' '}
                  <Text code>shippingService</Text>
                </td>
                <td>Hãng / dịch vụ vận chuyển</td>
              </tr>
              <tr>
                <td>
                  <Text code>totalCost</Text>
                </td>
                <td>Tổng chi phí đơn (đơn bị hủy hiển thị 0)</td>
              </tr>
              <tr>
                <td>
                  <Text code>createdAt</Text>
                </td>
                <td>Thời điểm tạo</td>
              </tr>
              <tr>
                <td>
                  <Text code>notes</Text>, <Text code>shipWithHardTube</Text>
                </td>
                <td>Ghi chú / tùy chọn đóng gói</td>
              </tr>
              <tr>
                <td>
                  <Text code>items[]</Text>
                </td>
                <td>
                  Line items: <Text code>designSku</Text>,{' '}
                  <Text code>designUrl</Text>, <Text code>size</Text>,{' '}
                  <Text code>quantity</Text>, <Text code>itemCost</Text>,{' '}
                  <Text code>variant</Text>
                </td>
              </tr>
            </tbody>
          </ParamTable>
          <Paragraph type="secondary">
            API cố ý không trả một số trường nội bộ (ví dụ chi phí ship hệ
            thống, label PDF nội bộ) — chỉ các trường phù hợp để seller tích
            hợp.
          </Paragraph>
        </Section>

        <Section>
          <Title level={4}>Ví dụ nhanh</Title>
          <Paragraph>Lấy 20 đơn đầu tiên:</Paragraph>
          <Pre>
{`curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${config.API_HOST}/v1/orders?page=1&perPage=20"`}
          </Pre>
          <Paragraph>Lọc đơn đã ship trong khoảng ngày:</Paragraph>
          <Pre>
{`curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${config.API_HOST}/v1/orders?status=shipped&fromDate=2026-01-01&toDate=2026-01-31&byDate=DESC"`}
          </Pre>
          <Paragraph>Lấy chi tiết một đơn:</Paragraph>
          <Pre>
{`curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${config.API_HOST}/v1/orders/ORDER_UUID"`}
          </Pre>
          <Paragraph>
            Tạo đơn hàng loạt (body JSON lưu trong file{' '}
            <Text code>orders.json</Text>):
          </Paragraph>
          <Pre>
{`curl -X POST -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d @orders.json \\
  "${config.API_HOST}/v1/orders/bulk"`}
          </Pre>
          <Paragraph>Xem size / variant hợp lệ của một sản phẩm:</Paragraph>
          <Pre>
{`curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${config.API_HOST}/v1/sizes?product=sticker"`}
          </Pre>
        </Section>

        <Section>
          <Title level={4}>Lưu ý bảo mật</Title>
          <BulletList>
            <li>
              Chỉ lưu token trên server của bạn; không hard-code trên app
              mobile / website công khai nếu người dùng cuối có thể xem được.
            </li>
            <li>
              Khi nhân viên nghỉ việc hoặc nghi lộ token →{' '}
              <Text strong>Thu hồi</Text> hoặc <Text strong>Đổi token</Text>.
            </li>
            <li>
              Token đăng nhập web (JWT) và API token là hai thứ khác nhau —
              app tích hợp chỉ dùng API token <Text code>stk_…</Text>.
            </li>
          </BulletList>
        </Section>
      </Content>
    </PageContainer>
  );
};

export default ApiAccess;

const Content = styled.div`
  padding: 0 4px 40px;
  max-width: 960px;
`;

const Section = styled.section`
  margin-bottom: 36px;
`;

const SubTitle = styled.h5`
  margin: 16px 0 8px;
  font-size: 15px;
  font-weight: 600;
`;

const NewTag = styled(Tag)`
  margin-left: 4px;
  vertical-align: middle;
  font-size: 11px;
  line-height: 18px;
  font-weight: 600;
`;

const CreateRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TokenValue = styled(Text)`
  word-break: break-all;
`;

const Pre = styled.pre`
  background: #f6f8fa;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  padding: 12px 16px;
  overflow-x: auto;
  font-size: 13px;
  margin-bottom: 12px;
`;

const BulletList = styled.ul`
  padding-left: 20px;
  margin: 0 0 8px;
  color: rgba(0, 0, 0, 0.65);

  li {
    margin-bottom: 6px;
  }
`;

const ParamTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin: 12px 0 16px;

  th,
  td {
    border: 1px solid var(--color-border, #e5e7eb);
    padding: 8px 10px;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f6f8fa;
    font-weight: 600;
  }

  td:first-child {
    white-space: nowrap;
    width: 1%;
  }
`;
